import { useState, useCallback, useMemo } from 'react';
import { toDateOnlyString } from '../../../../../utils/dateUtils';
import { parseChangedFields } from '../../../../../utils/dealerApprovalEnums';

const KARSAN_BRAND = { id: 1, brandShort: 'KARSAN', displayValue: 'KARSAN' };

let blockSeq = 0;
const nextBlockKey = () => `block_${Date.now()}_${++blockSeq}`;

const toBool = (v) => Boolean(Number(v));

const createAddressBlock = (overrides = {}) => ({
    key: nextBlockKey(),
    dealerAddressBrandId: null,
    dealerAddressId: null,
    selectedAddress: null,
    sapAddrId: '',
    siteUseId: 1,
    addressName: '',
    address: '',
    country: '',
    taxNo: '',
    taxOffice: '',
    addressIsActive: true,
    selectedBrand: KARSAN_BRAND,
    brandIsActive: true,
    contractStartDate: null,
    contractEndDate: null,
    rowState: 0,
    changedFields: [],
    tabChanges: [],
    isPrimary: false,
    ...overrides
});

const mapAddressToBlock = (addr) => {
    if (!addr) return createAddressBlock();
    const changed = parseChangedFields(addr.changedFieldsJson);
    const changedFields = changed && typeof changed === 'object' ? Object.keys(changed) : [];
    const brandName = addr.brandShort || addr.brandName || '';
    const sapAddrId = addr.sapAddrId != null ? String(addr.sapAddrId) : '';
    return createAddressBlock({
        key: addr.dealerAddressBrandId != null ? `dab_${addr.dealerAddressBrandId}` : nextBlockKey(),
        dealerAddressBrandId: addr.dealerAddressBrandId ?? null,
        dealerAddressId: addr.dealerAddressId ?? null,
        sapAddrId,
        siteUseId: addr.siteUseId ?? addr.sapAddrId ?? 1,
        addressName: addr.addressName ?? '',
        address: addr.address ?? '',
        country: addr.country ?? '',
        taxNo: addr.taxNo ?? '',
        taxOffice: addr.taxOffice ?? '',
        addressIsActive: toBool(addr.addressIsActive ?? 1),
        selectedAddress: sapAddrId
            ? {
                  id: Number(addr.sapAddrId),
                  displayValue: addr.address || addr.addressName || '',
                  country: addr.country
              }
            : null,
        selectedBrand: addr.brandId ? { id: addr.brandId, brandShort: brandName, displayValue: brandName } : KARSAN_BRAND,
        brandIsActive: toBool(addr.brandIsActive ?? 1),
        contractStartDate: addr.contractStartDate ? new Date(addr.contractStartDate) : null,
        contractEndDate: addr.contractEndDate ? new Date(addr.contractEndDate) : null,
        rowState: addr.rowState ?? 0,
        changedFields,
        tabChanges: Array.isArray(addr.tabChanges) ? addr.tabChanges : [],
        isPrimary: addr.isPrimary === true
    });
};

const mapScalarDetailToBlock = (detail) =>
    mapAddressToBlock({
        dealerAddressBrandId: detail.dealerAddressBrandId,
        dealerAddressId: detail.dealerAddressId,
        sapAddrId: detail.sapAddrId,
        siteUseId: detail.siteUseId,
        addressName: detail.addressName,
        address: detail.address,
        country: detail.country,
        taxNo: detail.taxNo,
        taxOffice: detail.taxOffice,
        addressIsActive: detail.addressIsActive,
        brandId: detail.brandId,
        brandName: detail.brandName,
        brandShort: detail.brandShort,
        brandIsActive: detail.brandIsActive,
        contractStartDate: detail.contractStartDate,
        contractEndDate: detail.contractEndDate,
        rowState: detail.rowState,
        changedFieldsJson: detail.changedFieldsJson,
        tabChanges: detail.tabChanges,
        isPrimary: true
    });

const mapBlockToApi = (block) => ({
    sapAddrId: Number(block.sapAddrId) || undefined,
    siteUseId: block.siteUseId ? Number(block.siteUseId) : Number(block.sapAddrId) || undefined,
    addressName: block.addressName,
    address: block.address,
    country: block.country,
    taxNo: block.taxNo || null,
    taxOffice: block.taxOffice || null,
    brandId: block.selectedBrand?.id,
    contractStartDate: toDateOnlyString(block.contractStartDate),
    contractEndDate: toDateOnlyString(block.contractEndDate),
    addressIsActive: block.addressIsActive ? 1 : 0,
    brandIsActive: block.brandIsActive ? 1 : 0
});

const createInitialState = () => {
    const block = createAddressBlock();
    return {
        selectedDealer: null,
        sapDealerId: '',
        erpDealerInteger: '',
        fullName: '',
        shortName: '',
        showPrice: true,
        showDiscount: true,
        showInvoices: true,
        canUseFullService: false,
        dealerIsActive: true,
        addresses: [block],
        selectedBlockKey: block.key
    };
};

/**
 * Birleşik bayi kaydı (bayi + N adres+marka bloğu) formu için state yönetimi.
 * Bayi alanları tekil; adres+marka `addresses[]` içinde tutulur. Seçili blok
 * (selectedBlockKey) sekme çağrılarının dealerAddressBrandId anahtarını üretir.
 */
const useDealerRegistrationForm = () => {
    const [submitted, setSubmitted] = useState(false);
    const [formValues, setFormValues] = useState(createInitialState);

    const { sapDealerId, erpDealerInteger, fullName, shortName, showPrice, showDiscount, showInvoices, canUseFullService, dealerIsActive, addresses, selectedBlockKey } = formValues;

    const selectedBlock = useMemo(() => addresses.find((b) => b.key === selectedBlockKey) || addresses[0] || null, [addresses, selectedBlockKey]);

    const setField = useCallback(
        (key) => (value) =>
            setFormValues((prev) => ({
                ...prev,
                [key]: typeof value === 'function' ? value(prev[key]) : value
            })),
        []
    );

    const setters = useMemo(
        () => ({
            setSelectedDealer: setField('selectedDealer'),
            setSapDealerId: setField('sapDealerId'),
            setErpDealerInteger: setField('erpDealerInteger'),
            setFullName: setField('fullName'),
            setShortName: setField('shortName'),
            setShowPrice: setField('showPrice'),
            setShowDiscount: setField('showDiscount'),
            setShowInvoices: setField('showInvoices'),
            setCanUseFullService: setField('canUseFullService'),
            setDealerIsActive: setField('dealerIsActive'),
            setSelectedBlockKey: setField('selectedBlockKey')
        }),
        [setField]
    );

    const setBlockField = useCallback(
        (blockKey, field) => (value) =>
            setFormValues((prev) => ({
                ...prev,
                addresses: prev.addresses.map((b) => {
                    if (b.key !== blockKey) return b;
                    const nextVal = typeof value === 'function' ? value(b[field]) : value;
                    return { ...b, [field]: nextVal };
                })
            })),
        []
    );

    const addBlock = useCallback(() => {
        const block = createAddressBlock();
        setFormValues((prev) => ({
            ...prev,
            addresses: [...prev.addresses, block],
            selectedBlockKey: block.key
        }));
    }, []);

    const removeBlock = useCallback((blockKey) => {
        setFormValues((prev) => {
            const target = prev.addresses.find((b) => b.key === blockKey);
            if (!target || target.dealerAddressBrandId != null) return prev;
            const remaining = prev.addresses.filter((b) => b.key !== blockKey);
            const addressesNext = remaining.length > 0 ? remaining : [createAddressBlock()];
            const selectedBlockKeyNext = prev.selectedBlockKey === blockKey ? addressesNext[0].key : prev.selectedBlockKey;
            return { ...prev, addresses: addressesNext, selectedBlockKey: selectedBlockKeyNext };
        });
    }, []);

    const onBlockAddressChange = useCallback(
        (blockKey) => (option) => {
            setFormValues((prev) => ({
                ...prev,
                addresses: prev.addresses.map((b) => {
                    if (b.key !== blockKey) return b;
                    if (!option) {
                        return { ...b, selectedAddress: null, sapAddrId: '', addressName: '', address: '', country: '', siteUseId: 1 };
                    }
                    return {
                        ...b,
                        selectedAddress: option,
                        sapAddrId: option.id?.toString() ?? '',
                        siteUseId: option.id ?? b.siteUseId,
                        addressName: option.displayValue ?? '',
                        address: option.displayValue ?? '',
                        ...(option.country && { country: option.country })
                    };
                })
            }));
        },
        []
    );

    const onBlockContractStartDateChange = useCallback(
        (blockKey) => (value) => {
            setFormValues((prev) => ({
                ...prev,
                addresses: prev.addresses.map((b) => {
                    if (b.key !== blockKey) return b;
                    return {
                        ...b,
                        contractStartDate: value,
                        contractEndDate: value && b.contractEndDate && b.contractEndDate < value ? null : b.contractEndDate
                    };
                })
            }));
        },
        []
    );

    const resetForm = useCallback(() => {
        setSubmitted(false);
        setFormValues(createInitialState());
    }, []);

    const setFormData = useCallback((detail) => {
        if (!detail) return;
        const blocks = Array.isArray(detail.addresses) && detail.addresses.length > 0 ? detail.addresses.map(mapAddressToBlock) : [mapScalarDetailToBlock(detail)];

        setFormValues((prev) => {
            const prevSelected = prev.addresses.find((b) => b.key === prev.selectedBlockKey);
            const matchById = prevSelected?.dealerAddressBrandId != null ? blocks.find((b) => b.dealerAddressBrandId === prevSelected.dealerAddressBrandId) : null;
            const matchByAddress = prevSelected?.sapAddrId ? blocks.find((b) => String(b.sapAddrId) === String(prevSelected.sapAddrId)) : null;
            const primary = blocks.find((b) => b.isPrimary) || blocks[0];
            const selected = matchById || matchByAddress || primary;

            return {
                ...prev,
                sapDealerId: detail.sapDealerId?.toString() ?? '',
                erpDealerInteger: detail.erpDealerInteger?.toString() ?? '',
                fullName: detail.fullName ?? detail.dealerFullName ?? '',
                shortName: detail.shortName ?? detail.dealerShortName ?? '',
                showPrice: toBool(detail.showPrice),
                showDiscount: toBool(detail.showDiscount),
                showInvoices: toBool(detail.showInvoices),
                canUseFullService: toBool(detail.canUseFullService),
                dealerIsActive: toBool(detail.dealerIsActive ?? 1),
                selectedDealer: {
                    id: detail.dealerId ?? detail.sapDealerId,
                    sapDealerId: detail.sapDealerId,
                    name: detail.dealerFullName ?? detail.fullName ?? '',
                    displayValue: detail.dealerFullName ?? detail.fullName ?? '',
                    erpDealerId: detail.erpDealerInteger ?? detail.erpDealerNumber
                },
                addresses: blocks,
                selectedBlockKey: selected?.key
            };
        });
    }, []);

    const applyStagedPayload = useCallback((payload) => {
        if (!payload || typeof payload !== 'object') return;
        const fieldMap = [['fullName'], ['shortName'], ['erpDealerInteger', (v) => v.toString()], ['showPrice', toBool], ['showDiscount', toBool], ['showInvoices', toBool], ['canUseFullService', toBool], ['dealerIsActive', toBool]];
        setFormValues((prev) => {
            const next = { ...prev };
            fieldMap.forEach(([key, transform]) => {
                const value = payload[key];
                if (value != null) next[key] = transform ? transform(value) : value;
            });
            return next;
        });
    }, []);

    const onDealerChange = useCallback((value) => {
        if (!value) {
            const block = createAddressBlock();
            setFormValues((prev) => ({
                ...prev,
                selectedDealer: null,
                sapDealerId: '',
                erpDealerInteger: '',
                fullName: '',
                shortName: '',
                addresses: [block],
                selectedBlockKey: block.key
            }));
            return;
        }
        const block = createAddressBlock();
        setFormValues((prev) => ({
            ...prev,
            selectedDealer: value,
            sapDealerId: value.sapDealerId?.toString() ?? '',
            erpDealerInteger: (value.erpDealerId ?? value.erpDealerInteger ?? value.erpDealerNumber ?? value.sapDealerId ?? '').toString(),
            fullName: value.name || value.displayValue || '',
            shortName: value.SortName ?? value.sortName ?? value.name ?? value.displayValue ?? '',
            addresses: [block],
            selectedBlockKey: block.key
        }));
    }, []);

    const validate = () => {
        const errors = [];
        if (!sapDealerId || !fullName?.trim()) {
            errors.push('common.requiredField');
        }
        if (!addresses?.length) {
            errors.push('dealerRegistration.blockRequired');
            return errors;
        }
        const sapAddrIds = new Set();
        let missingAddress = false;
        let missingBrand = false;
        let missingStart = false;
        let duplicate = false;
        addresses.forEach((b) => {
            if (!b.sapAddrId) missingAddress = true;
            if (!b.selectedBrand?.id) missingBrand = true;
            if (!b.contractStartDate) missingStart = true;
            if (b.sapAddrId) {
                const addrKey = String(b.sapAddrId);
                if (sapAddrIds.has(addrKey)) duplicate = true;
                sapAddrIds.add(addrKey);
            }
        });
        if (missingAddress) errors.push('dealerRegistration.addressRequired');
        if (missingBrand) errors.push('dealerRegistration.brandRequired');
        if (missingStart) errors.push('common.requiredField');
        if (duplicate) errors.push('dealerRegistration.duplicateAddress');
        return errors;
    };

    const isValid = () => validate().length === 0;

    const buildDealerFields = (languageId) => ({
        languageId,
        sapDealerId: Number(sapDealerId) || undefined,
        erpDealerInteger: erpDealerInteger ? Number(erpDealerInteger) : undefined,
        fullName,
        shortName,
        showPrice: showPrice ? 1 : 0,
        showDiscount: showDiscount ? 1 : 0,
        showInvoices: showInvoices ? 1 : 0,
        canUseFullService: canUseFullService ? 1 : 0,
        dealerIsActive: dealerIsActive ? 1 : 0
    });

    const buildPayload = (languageId) => ({
        ...buildDealerFields(languageId),
        addresses: addresses.map(mapBlockToApi)
    });

    const buildUpdatePayload = (languageId) => ({
        ...buildDealerFields(languageId),
        addresses: addresses.map((b) => ({
            dealerAddressBrandId: b.dealerAddressBrandId ?? null,
            ...mapBlockToApi(b)
        }))
    });

    return {
        ...formValues,
        ...setters,
        selectedBlock,
        submitted,
        setSubmitted,
        setBlockField,
        addBlock,
        removeBlock,
        onBlockAddressChange,
        onBlockContractStartDateChange,
        resetForm,
        setFormData,
        applyStagedPayload,
        onDealerChange,
        validate,
        isValid,
        buildPayload,
        buildUpdatePayload
    };
};

export default useDealerRegistrationForm;
